import { Combobox, ComboboxPopup, ComboboxWidget } from '@angular/aria/combobox';
import { Grid, GridCell, GridRow } from '@angular/aria/grid';
import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { filter, fromEvent } from 'rxjs';
import { BaseComponent } from '../../../components/base/base.component';
import { IconButtonDirective } from '../../../components/button/icon/icon-button.directive';
import { injectDialogHandle } from '../../../components/dialog/dialog.types';
import {
    GlobalSearchContext,
    GlobalSearchEntry,
    GlobalSearchSelection,
    SearchSection,
    SearchSectionStatus,
} from '../../models/global-search.model';
import { SearchResultAction } from '../../models/search-result.model';
import { GlobalSearchService } from '../../services/global-search.service';

/** Separates the entry key from the action id in secondary-action cell ids. */
const ACTION_ID_SEPARATOR = '::';

/** A section enriched with grid-addressable entries. */
interface SectionView extends SearchSection {
  readonly entries: readonly GlobalSearchEntry[];
}

/**
 * The command-palette content rendered inside the simple dialog. The Aria combobox
 * keeps focus in the input and forwards navigation keys into an ARIA grid in
 * `activedescendant` mode: ↑/↓ move between result rows, ←/→ move between a row's
 * cells (the result itself plus one cell per secondary action), Enter activates
 * the active cell. `GridCell` marks the active cell with `data-active`, which the
 * stylesheet uses for the visual highlight.
 */
@Component({
  selector: 'immo-global-search-dialog',
  templateUrl: './global-search-dialog.component.html',
  styleUrl: './global-search-dialog.component.css',
  imports: [
    Combobox,
    ComboboxPopup,
    ComboboxWidget,
    Grid,
    GridRow,
    GridCell,
    MatIconModule,
    MatRippleModule,
    IconButtonDirective,
  ],
  providers: [GlobalSearchService],
})
export class GlobalSearchDialogComponent extends BaseComponent implements OnInit {
  // -----------------------------------------------------------------------------------------------------
  // @ DEPENDENCIES
  // -----------------------------------------------------------------------------------------------------
  protected readonly search = inject(GlobalSearchService);

  protected readonly dialog = injectDialogHandle<GlobalSearchSelection, GlobalSearchContext>();

  private readonly document = inject(DOCUMENT);

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Whether the combobox popup (the result panel) is expanded. Forced open — the panel is the palette. */
  protected readonly popupExpanded: WritableSignal<boolean> = signal(true);

  // -----------------------------------------------------------------------------------------------------
  // @ COMPUTED VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Sections with flattened, keyed entries; empty successful sections are hidden. */
  protected readonly sectionViews: Signal<SectionView[]> = computed(() =>
    this.search
      .sections()
      .map(
        (section): SectionView => ({
          ...section,
          entries: section.items.map(
            (item): GlobalSearchEntry => ({
              key: `${section.connector.id}:${item.id}`,
              connectorId: section.connector.id,
              item,
            }),
          ),
        }),
      )
      .filter(
        (section) => section.status !== SearchSectionStatus.Success || section.entries.length > 0,
      ),
  );

  /** Fast lookup from a grid-cell id (entry key, action ids stripped) back to the entry. */
  protected readonly entryByKey: Signal<Map<string, GlobalSearchEntry>> = computed(() => {
    const map = new Map<string, GlobalSearchEntry>();

    for (const section of this.sectionViews()) {
      for (const entry of section.entries) {
        map.set(entry.key, entry);
      }
    }

    return map;
  });

  /** Scope chips derived from the active scope. */
  protected readonly scopeChips: Signal<{ key: string; label: string }[]> = computed(() => {
    const scope = this.search.scope();

    return Object.keys(scope.context ?? {}).map((key) => ({
      key,
      label: scope.labels?.[key] ?? key,
    }));
  });

  /** Whether the current query produced no results anywhere and nothing is loading. */
  protected readonly isEmpty: Signal<boolean> = computed(
    () =>
      !this.IsNullOrUndefinedOrEmpty(this.search.query().trim()) &&
      !this.search.isLoading() &&
      this.search.totalCount() === 0,
  );

  /** Whether to show the remembered queries — only while the input is empty. */
  protected readonly showRecentSearches: Signal<boolean> = computed(
    () =>
      this.IsNullOrUndefinedOrEmpty(this.search.query().trim()) &&
      this.search.recentSearches().length > 0,
  );

  /** Screen-reader announcement of the current result state. */
  protected readonly liveMessage: Signal<string> = computed(() => {
    if (this.IsNullOrUndefinedOrEmpty(this.search.query().trim())) {
      return '';
    }

    if (this.search.isLoading()) {
      return 'Suche läuft…';
    }

    const total = this.search.totalCount();

    return total === 0 ? `Keine Ergebnisse für ${this.search.query()}` : `${total} Ergebnisse`;
  });

  // -----------------------------------------------------------------------------------------------------
  // @ EFFECTS
  // -----------------------------------------------------------------------------------------------------

  /**
   * The panel is the palette — it must never collapse while the dialog lives.
   * The combobox writes `expanded = false` during its own initialization (and on
   * blur/selection); deriving "close the dialog" from that signal caused the
   * dialog to close immediately after opening. Instead we force the popup back
   * open on every collapse attempt; closing the dialog is handled explicitly
   * by the Escape listener in the constructor and by `executeAction`.
   */
  private readonly keepPanelOpen = effect(() => {
    if (!this.popupExpanded()) {
      this.popupExpanded.set(true);
    }
  });

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor() {
    super();

    // Capture phase so this wins even when the combobox consumes Escape itself —
    // guarantees a single Esc closes the palette instead of only collapsing the popup.
    fromEvent<KeyboardEvent>(this.document, 'keydown', { capture: true })
      .pipe(
        filter((event) => event.key === 'Escape'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        event.preventDefault();
        event.stopPropagation();

        this.dialog.close(undefined);
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ LIFECYCLE HOOKS
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.search.initialize(this.dialog.context);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PROTECTED METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Commits the grid's active cell. A main cell runs the entry's primary action;
   * an action cell (id `entryKey::actionId`) runs that specific action. Wired
   * to `(keydown.enter)` on the grid.
   *
   * @param activeCellId - The grid's current `activeDescendant` id.
   * @returns {void}
   */
  protected onCommit(activeCellId: string | undefined): void {
    const resolved = this.resolveCell(activeCellId);

    if (this.IsNullOrUndefined(resolved)) {
      return;
    }

    this.executeAction(resolved.entry, resolved.action);
  }

  /**
   * Runs the primary action of a clicked main cell.
   *
   * @param entry - The clicked entry.
   * @returns {void}
   */
  protected onEntryClick(entry: GlobalSearchEntry): void {
    this.executeAction(entry, entry.item.actions[0]);
  }

  /**
   * Runs a specific action from its cell, bypassing the main-cell click.
   *
   * @param event - The click event (propagation is stopped so the row commit doesn't also fire).
   * @param entry - The entry the cell belongs to.
   * @param action - The action to run.
   * @returns {void}
   */
  protected onActionCellClick(
    event: Event,
    entry: GlobalSearchEntry,
    action: SearchResultAction,
  ): void {
    event.stopPropagation();

    this.executeAction(entry, action);
  }

  /**
   * Ctrl/Cmd+Enter shortcut: runs the second action of the entry the active cell
   * belongs to, regardless of which cell in the row is active.
   *
   * @param event - The keyboard event, consumed so Enter isn't double-handled.
   * @param activeCellId - The grid's current `activeDescendant` id.
   * @returns {void}
   */
  protected onSecondaryCommit(event: Event, activeCellId: string | undefined): void {
    event.preventDefault();
    event.stopPropagation();

    const resolved = this.resolveCell(activeCellId);

    if (this.IsNullOrUndefined(resolved)) {
      return;
    }

    this.executeAction(
      resolved.entry,
      resolved.entry.item.actions[1] ?? resolved.entry.item.actions[0],
    );
  }

  /**
   * Removes a scope chip. `mousedown.preventDefault` in the template keeps the
   * combobox from collapsing focus.
   *
   * @param key - The scope key to remove.
   * @returns {void}
   */
  protected onRemoveScope(key: string): void {
    this.search.removeScope(key);
  }

  /**
   * Fills the input with a remembered query — the search then runs through the
   * normal debounced pipeline exactly as if the user had typed it.
   *
   * @param query - The remembered query to re-run.
   * @returns {void}
   */
  protected onRecentSearchClick(query: string): void {
    this.search.query.set(query);
  }

  /**
   * Restricts the palette to one connector ("Nur: Aufgaben"). `mousedown.preventDefault`
   * in the template keeps the combobox focus in the input.
   *
   * @param id - The connector id to filter to.
   * @param label - The connector's section label, reused for the chip.
   * @returns {void}
   */
  protected onFilterConnector(id: string, label: string): void {
    this.search.connectorFilter.set({ id, label });
  }

  /**
   * Clears the connector filter and searches all sections again.
   *
   * @returns {void}
   */
  protected onRemoveConnectorFilter(): void {
    this.search.connectorFilter.set(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Resolves a grid-cell id back to its entry and the action the cell stands for.
   *
   * @param cellId - The cell id (`entryKey` or `entryKey::actionId`).
   * @returns {{ entry: GlobalSearchEntry; action: SearchResultAction } | undefined} The resolved pair.
   */
  private resolveCell(
    cellId: string | undefined,
  ): { entry: GlobalSearchEntry; action: SearchResultAction } | undefined {
    if (this.IsNullOrUndefinedOrEmpty(cellId)) {
      return undefined;
    }

    const [entryKey, actionId] = cellId.split(ACTION_ID_SEPARATOR);
    const entry = this.entryByKey().get(entryKey);

    if (this.IsNullOrUndefined(entry)) {
      return undefined;
    }

    const action = this.IsNullOrUndefinedOrEmpty(actionId)
      ? entry.item.actions[0]
      : (entry.item.actions.find((candidate) => candidate.id === actionId) ??
        entry.item.actions[0]);

    return { entry, action };
  }

  /**
   * Executes an action and resolves the dialog with the selection unless the
   * action asked to keep the palette open.
   *
   * @param entry - The committed entry.
   * @param action - The action to run.
   * @returns {void}
   */
  private executeAction(entry: GlobalSearchEntry, action: SearchResultAction | undefined): void {
    if (this.IsNullOrUndefined(action)) {
      return;
    }

    // A committed result proves the query was useful — remember it for next time
    this.search.rememberSearch(this.search.query());

    const selection: GlobalSearchSelection = {
      item: entry.item,
      action,
      connectorId: entry.connectorId,
    };

    Promise.resolve(
      action.execute(entry.item, {
        scope: this.search.scope(),
        closeDialog: () => this.dialog.close(selection),
        setScope: (key, value, label) => this.search.addScope(key, value, label),
      }),
    ).then(() => {
      if (!action.keepOpen) {
        this.dialog.close(selection);
      }
    });
  }
}
